import toast from "react-hot-toast";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import type { UserTestDto } from "~/data/user-test/user-test.dto";

import { socket } from "~/data/core/socket";
import whistleSound from "~/ui/shared/assets/sounds/whistle.mp3";
import {
  finishChecklist,
  getUnlockedExams,
  startUserTest,
  UnlockedExam,
  updateUserTest,
} from "~/data/user-test/user-test.service";

import { Talk } from "./talk";
import { Exams } from "./exams";
import { Timer } from "./timer";
import { Button } from "../shared/button";
import { Dialog } from "../shared/dialog";
import { FeedbackForm } from "./feedback-form";
import { Accordion } from "../shared/accordion";
import { ShareIcon } from "../shared/icons/share";
import { IconButton } from "../shared/icon-button";
import { HtmlContent } from "../shared/html-content";
import { useUserTest } from "./queries/use-user-test";
import { useUser } from "../shared/queries/user-query";
import { PerformanceModal } from "./performance-modal";
import { ChecklistQuestions } from "./checklist-questions";

export function Training() {
  const params = useParams();
  return <TrainingUserTest key={params.id} />; // resets full state on change user test
}

export function TrainingUserTest() {
  const params = useParams();
  const queryClient = useQueryClient();
  const userTest = useUserTest(params.id!);
  const startMutation = useMutation({
    mutationFn: () => startUserTest(params.id!),
    onSuccess: (partial) =>
      queryClient.setQueryData<UserTestDto>(
        ["user_test", params.id],
        (data) => data && { ...data, ...partial }
      ),
  });
  const finishChecklistMutation = useMutation({
    mutationFn: () => finishChecklist(params.id!),
    onSuccess: (partial) =>
      queryClient.setQueryData<UserTestDto>(
        ["user_test", params.id],
        (data) => data && { ...data, ...partial }
      ),
  });
  const user = useUser();

  const isActor = userTest.user_id !== user.id;

  // Subscribes to ActionCable channel
  useEffect(() => {
    const identifier = { channel: "UserTestChannel", room: params.id };
    socket.send({
      command: "subscribe",
      identifier,
    });
    socket.send({ command: "message", identifier, data: { action: "ready" } });
    return () => socket.send({ command: "unsubscribe", identifier });
  }, [params.id]);

  useEffect(
    () =>
      socket.on("ready", (partial) =>
        queryClient.setQueryData<UserTestDto>(
          ["user_test", params.id],
          (data) => data && { ...data, ...partial }
        )
      ),
    [params.id, queryClient]
  );

  useEffect(
    () =>
      socket.on("start_test", (partial) => {
        // Audio effect on start test
        const audio = new Audio(whistleSound);
        audio.play();

        queryClient.setQueryData<UserTestDto>(
          ["user_test", params.id],
          (data) => data && { ...data, ...partial }
        );
      }),
    [params.id, queryClient]
  );
  useEffect(
    () =>
      socket.on("timer_update", (partial) =>
        queryClient.setQueryData<UserTestDto>(
          ["user_test", params.id],
          (data) => data && { ...data, ...partial }
        )
      ),
    [params.id, queryClient]
  );
  useEffect(
    () =>
      socket.on("finish_test", (partial) =>
        queryClient.setQueryData<UserTestDto>(
          ["user_test", params.id],
          (data) => data && { ...data, ...partial }
        )
      ),
    [params.id, queryClient]
  );

  const { data: exams } = useQuery({
    queryKey: ["unlocked_exams", userTest.id],
    queryFn: () => getUnlockedExams(userTest.id),
    enabled: Boolean(userTest.started_at && !userTest.finished_at) && !isActor,
    refetchInterval: 2.5 * 1000, // 2.5s interval (fallback in case ws doesn't receive show event)
    initialData: userTest.test.test_exams ?? [],
  });
  useEffect(
    () =>
      socket.on("show_test_exam", (exam) => {
        if (isActor) return;
        queryClient.setQueryData<UnlockedExam[]>(
          ["unlocked_exams", userTest.id],
          (exams = []) => {
            const alreadyThere = exams.some((it) => it.id === exam.id);
            if (alreadyThere) return exams;
            return [...exams, exam];
          }
        );
      }),
    [isActor, queryClient, userTest.id]
  );

  const [showPerformanceModal, setShowPerformanceModal] = useState(false);
  useEffect(() => {
    if (userTest.performance) setShowPerformanceModal(true);
  }, [userTest.performance]);

  const finishMutation = useMutation({
    mutationFn: () =>
      updateUserTest(params.id!, { finished_at: new Date().toISOString() }),
    onSuccess: (partial) =>
      queryClient.setQueryData<UserTestDto>(
        ["user_test", params.id],
        (data) => data && { ...data, ...partial }
      ),
  });

  console.log({ userTest });

  const [feedbackOpen, setFeedbackOpen] = useState(false);

  return (
    <div className="flex gap-5 p-5">
      {userTest.test_type === "ai" && (
        <div>
          {/* <div className="flex items-center justify-center gap-3 mb-3">
          <IconButton
            type="button"
            aria-label="Compartilhar Link"
            onClick={() =>
              navigator.share({
                url: `${window.location.href}?code=${userTest.join_station_code}`,
              })
            }
            icon={<IoMdShare size={20} />}
          />
          <IconButton
            type="button"
            aria-label="Copiar Link"
            onClick={() =>
              navigator.clipboard
                .writeText(
                  `${window.location.href}?code=${userTest.join_station_code}`
                )
                .then(() => toast("Copiado!"))
            }
            icon={<IoMdCopy size={20} />}
          />
        </div> */}

          <div className="sticky top-5">
            <Talk />
          </div>
        </div>
      )}

      <div className="flex-1">
        <div className="flex items-center mb-3">
          <h2 className="font-semibold text-3xl">Pratique Revalida</h2>
          <div className="flex items-center gap-5">
            <span className="text-sm ml-5 bg-green-400 py-px px-2 rounded-[4px]">
              {userTest.test.category_name}
            </span>
            <IconButton
              type="button"
              aria-label="Compartilhar Link"
              onClick={() => {
                const url = `${window.location.href}?code=${userTest.join_station_code}`;
                if (navigator.canShare({ url })) {
                  navigator.share({ url });
                } else {
                  navigator.clipboard
                    .writeText(url)
                    .then(() => toast("Copiado!"));
                }
              }}
              icon={<ShareIcon size={32} />}
            />
            <IconButton
              type="button"
              aria-label="Feedback"
              onClick={() => setFeedbackOpen(true)}
              icon={
                <svg
                  width="32"
                  height="32"
                  viewBox="0 0 32 32"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <rect width="32" height="32" rx="3" fill="#F4F4F4" />
                  <path
                    d="M2.66699 29.3337V5.33366C2.66699 4.60033 2.92833 3.97277 3.45099 3.45099C3.97366 2.92921 4.60121 2.66788 5.33366 2.66699H26.667C27.4003 2.66699 28.0283 2.92833 28.551 3.45099C29.0737 3.97366 29.3345 4.60121 29.3337 5.33366V21.3337C29.3337 22.067 29.0728 22.695 28.551 23.2177C28.0292 23.7403 27.4012 24.0012 26.667 24.0003H8.00033L2.66699 29.3337ZM16.0003 20.0003C16.3781 20.0003 16.695 19.8723 16.951 19.6163C17.207 19.3603 17.3345 19.0439 17.3337 18.667C17.3328 18.2901 17.2048 17.9737 16.9497 17.7177C16.6945 17.4617 16.3781 17.3337 16.0003 17.3337C15.6225 17.3337 15.3061 17.4617 15.051 17.7177C14.7959 17.9737 14.6679 18.2901 14.667 18.667C14.6661 19.0439 14.7941 19.3608 15.051 19.6177C15.3079 19.8745 15.6243 20.0021 16.0003 20.0003ZM14.667 14.667H17.3337V6.66699H14.667V14.667Z"
                    fill="#5B5B5B"
                  />
                </svg>
              }
            />
          </div>

          <Dialog open={feedbackOpen} onOpenChange={setFeedbackOpen}>
            <Dialog.Content className="w-11/12 max-w-xl">
              <FeedbackForm
                userTestId={userTest.id}
                onFinish={() => setFeedbackOpen(false)}
              />
            </Dialog.Content>
          </Dialog>

          <div className="ml-auto flex gap-4 items-center">
            {/* {!isActor && !userTest.started_at && (
              <Button variant="primary-1" className="h-14 w-72">
                Cancelar
              </Button>
            )} */}

            {!isActor && !userTest.started_at && (
              <Button
                loading={startMutation.isPending}
                onClick={() =>
                  startMutation.mutate(void 0, {
                    onSuccess: () =>
                      socket.send({
                        command: "message",
                        identifier: {
                          channel: "UserTestChannel",
                          room: params.id,
                        },
                        data: { action: "start_test" },
                      }),
                  })
                }
                variant="alternative-2"
                className="h-12 w-60"
              >
                Iniciar
              </Button>
            )}
            {!isActor && !userTest.finished_at && userTest.started_at && (
              <Button
                loading={startMutation.isPending}
                onClick={() =>
                  requestAnimationFrame(async () => {
                    const confirmed = window.confirm(
                      "Tem certeza que quer encerrar agora?"
                    );
                    if (!confirmed) return;
                    finishMutation.mutate();
                  })
                }
                variant="primary-1"
                className="h-12 w-60"
              >
                Finalizar
              </Button>
            )}
            {!userTest.finished_at && <Timer data={userTest} />}
          </div>
        </div>

        <section className="bg-white rounded-[20px] mb-5 p-6 shadow-card">
          <Accordion
            type="multiple"
            className="flex flex-col gap-3"
            defaultValue={["description"]}
          >
            <Accordion.Item
              label={<span className="uppercase">Cenário de atendimento</span>}
              value="description"
            >
              <HtmlContent content={userTest.test.acting_scenario} />
            </Accordion.Item>
            {!!userTest.test.tasks && (
              <Accordion.Item
                label={
                  <span className="uppercase text-start">{`Nos ${
                    userTest.time_to_complete_in_minutes ?? 10
                  } minutos de duração da Estação, você deverá executar as seguintes tarefas`}</span>
                }
                value="tasks"
              >
                <HtmlContent content={userTest.test.tasks} />
              </Accordion.Item>
            )}
            {isActor && userTest.test.actor_orientations && (
              <Accordion.Item
                label={<span className="uppercase">Orientação do ator</span>}
                value="orientation"
              >
                <HtmlContent content={userTest.test.actor_orientations} />
              </Accordion.Item>
            )}
            {!!exams?.length && (
              <Accordion.Item
                label={
                  <span className="uppercase">
                    Lista de exames ({exams.length})
                  </span>
                }
                value="exams"
              >
                <Exams
                  data={exams}
                  onUnlock={
                    isActor
                      ? (examId) => {
                          socket.send({
                            command: "message",
                            identifier: {
                              channel: "UserTestChannel",
                              room: userTest.id,
                            },
                            data: {
                              action: "show_test_exam",
                              test_exam_id: examId,
                            },
                          });
                        }
                      : undefined
                  }
                />
              </Accordion.Item>
            )}
            {userTest.test.test_questions && (
              <Accordion.Item
                label={
                  <span className="uppercase">
                    Padrão Esperado de Procedimento
                  </span>
                }
                value="checklist"
              >
                <ChecklistQuestions
                  questions={userTest.test.test_questions}
                  answers={userTest.test.selected_answers}
                  onFinish={() => finishChecklistMutation.mutate()}
                  finishing={finishChecklistMutation.isPending}
                />
              </Accordion.Item>
            )}
          </Accordion>

          {userTest.test.bibliographical_references && (
            <div className="flex items-center justify-between p-5 bg-[#f4f4f4] rounded-[10px] mt-5">
              <div>
                <h5 className="text-base font-alternative leading-5 font-semibold uppercase mb-[10px]">
                  Referência Bibliográfica
                </h5>
                <p className="flex gap-1 items-center uppercase font-bold text-red-500 text-[10px] leading-[14px] mb-4">
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M7.99968 11.333C8.18856 11.333 8.34701 11.269 8.47501 11.141C8.60301 11.013 8.66679 10.8548 8.66634 10.6663C8.6659 10.4779 8.6019 10.3197 8.47434 10.1917C8.34679 10.0637 8.18856 9.99967 7.99968 9.99967C7.81079 9.99967 7.65256 10.0637 7.52501 10.1917C7.39745 10.3197 7.33345 10.4779 7.33301 10.6663C7.33256 10.8548 7.39656 11.0132 7.52501 11.1417C7.65345 11.2701 7.81168 11.3339 7.99968 11.333ZM7.33301 8.66634H8.66634V4.66634H7.33301V8.66634ZM7.99968 14.6663C7.07745 14.6663 6.21079 14.4912 5.39968 14.141C4.58856 13.7908 3.88301 13.3159 3.28301 12.7163C2.68301 12.1168 2.20812 11.4112 1.85834 10.5997C1.50856 9.78812 1.33345 8.92145 1.33301 7.99967C1.33256 7.0779 1.50768 6.21123 1.85834 5.39967C2.20901 4.58812 2.6839 3.88256 3.28301 3.28301C3.88212 2.68345 4.58768 2.20856 5.39968 1.85834C6.21168 1.50812 7.07834 1.33301 7.99968 1.33301C8.92101 1.33301 9.78767 1.50812 10.5997 1.85834C11.4117 2.20856 12.1172 2.68345 12.7163 3.28301C13.3155 3.88256 13.7906 4.58812 14.1417 5.39967C14.4928 6.21123 14.6677 7.0779 14.6663 7.99967C14.665 8.92145 14.4899 9.78812 14.141 10.5997C13.7921 11.4112 13.3172 12.1168 12.7163 12.7163C12.1155 13.3159 11.4099 13.791 10.5997 14.1417C9.78945 14.4923 8.92279 14.6672 7.99968 14.6663ZM7.99968 13.333C9.48856 13.333 10.7497 12.8163 11.783 11.783C12.8163 10.7497 13.333 9.48856 13.333 7.99967C13.333 6.51079 12.8163 5.24967 11.783 4.21634C10.7497 3.18301 9.48856 2.66634 7.99968 2.66634C6.51079 2.66634 5.24968 3.18301 4.21634 4.21634C3.18301 5.24967 2.66634 6.51079 2.66634 7.99967C2.66634 9.48856 3.18301 10.7497 4.21634 11.783C5.24968 12.8163 6.51079 13.333 7.99968 13.333Z"
                      fill="#FA4F3B"
                    />
                  </svg>
                  Ao acessar as referências você terá acesso ao resultado do
                  diagnóstico
                </p>
                <div className="text-sm font-alternative">
                  <HtmlContent
                    content={userTest.test.bibliographical_references ?? ""}
                  />
                </div>
              </div>
              <Button className="ml-6 h-10 w-44" variant="primary-1">
                Acessar
              </Button>
            </div>
          )}
        </section>

        {/* <Accordion type="single" defaultValue="chat">
          <Accordion.Item value="chat" label="Chat para Atendimento">
            <div>
              <div className="bg-surface py-2 px-4">
                <input
                  type="text"
                  placeholder="Digite sua pergunta ou orientação"
                />
                <button type="button">
                  <IoMdSend />
                </button>
              </div>
            </div>
          </Accordion.Item>
        </Accordion> */}
      </div>

      {showPerformanceModal && userTest.performance && (
        <PerformanceModal
          data={userTest}
          performance={userTest.performance}
          onClose={() => setShowPerformanceModal(false)}
        />
      )}
    </div>
  );
}
